import { useCallback } from 'react';
import { Box } from '../../../components/ui';
import type { StepFormData } from '../../../types/knowledgeBase';
import { ImageUpload } from './ImageUpload';
import './StepEditor.css';

interface StepEditorProps {
  step: StepFormData;
  stepNumber: number;
  totalSteps: number;
  onChange: (step: StepFormData) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canRemove: boolean;
}

export function StepEditor({
  step,
  stepNumber,
  totalSteps,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canRemove,
}: StepEditorProps) {
  const handleFieldChange = useCallback(
    (field: keyof StepFormData, value: string | undefined) => {
      onChange({ ...step, [field]: value });
    },
    [step, onChange]
  );

  const handleImageChange = useCallback(
    (imageUrl: string | undefined) => {
      onChange({ ...step, imageUrl });
    },
    [step, onChange]
  );

  const canMoveUp = stepNumber > 1;
  const canMoveDown = stepNumber < totalSteps;

  return (
    <Box className="step-editor">
      <Box className="step-editor__header">
        <Box className="step-editor__header-left">
          <span className="step-editor__number">Step {stepNumber}</span>
          <Box className="step-editor__move-buttons">
            <button
              className="step-editor__move-btn"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              type="button"
              title="Move up"
              aria-label="Move step up"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 3v8M4 6l3-3 3 3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              className="step-editor__move-btn"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              type="button"
              title="Move down"
              aria-label="Move step down"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 11V3M4 8l3 3 3-3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </Box>
        </Box>
        {canRemove && (
          <button
            className="step-editor__remove"
            onClick={onRemove}
            type="button"
            title="Remove step"
            aria-label="Remove step"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 4l8 8M4 12l8-8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </Box>

      <Box className="step-editor__content">
        <Box className="step-editor__field">
          <label className="step-editor__label">Title</label>
          <input
            type="text"
            className="step-editor__input"
            value={step.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            placeholder="Brief step title (e.g., Open Settings)"
          />
        </Box>

        <Box className="step-editor__field">
          <label className="step-editor__label">Instructions</label>
          <textarea
            className="step-editor__textarea"
            value={step.content}
            onChange={(e) => handleFieldChange('content', e.target.value)}
            placeholder="Detailed instructions for this step..."
            rows={4}
          />
        </Box>

        <Box className="step-editor__field">
          <label className="step-editor__label">Image (optional)</label>
          <ImageUpload
            imageUrl={step.imageUrl}
            imageAlt={step.imageAlt}
            onChange={handleImageChange}
            onAltChange={(alt) => handleFieldChange('imageAlt', alt)}
          />
        </Box>
      </Box>
    </Box>
  );
}
